import {
  FC,
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { useSnackbar } from 'notistack';
import { chain, isEmpty, trimStart } from 'lodash';

import {
  useTypePartnerCompany,
  useSubscriptionFeatures,
  useRequest,
  useLazyRequest,
  useImporterOrdersProgressListener,
} from '~hooks/index';
import { getLogistiscZonesInfo } from '~services/logisticsZones';
import { LogisticZoneBase } from '~services/logisticsZones/types';
import { getIntegrations } from '~services/route';
import { importItemsFromIntegrations } from '~services/item';
import { AdminIntegration } from '~services/admin/integration/types';
import {
  ItemImportFromIntegrationsResponse,
  ItemImportFromIntegrationsBody,
} from '~services/item/types';
import { DataPaginateResponse } from '~globals/types';
import { PlanFeaturesTypes } from '~globals/types/enums';
import {
  ImporterOrdersProgressListenerStatus,
  ImporterOrdersProgressNotification,
} from '~hooks/useImporterOrdersProgressListener';
import { sortAlphabeticZoneByCode } from '~utils/logisiticsZones';

interface DialogImportOrdersFromIntegrationContextParams {
  onSubmitSuccess: () => void;
  onClose: () => void;
}

export type DialogImportOrdersFromIntegrationContextPanelsId =
  | 'notIntegrations'
  | 'presentation'
  | 'progress';

export interface DialogImportOrdersFromIntegrationContextLogisticsZones {
  list: LogisticZoneBase[];
  loading: boolean;
}

interface DialogImportOrdersFromIntegrationContextCurrentIntegrations {
  list: AdminIntegration[];
  loading: boolean;
}

interface DialogImportOrdersFromIntegrationContextImporterRequestItemsData {
  descriptions: string[];
}

interface DialogImportOrdersFromIntegrationContextImporterRequest {
  executeImport: (data: ItemImportFromIntegrationsBody) => Promise<void>;
  loading: boolean;
  isCompletedRequest: boolean;
  itemsData: DialogImportOrdersFromIntegrationContextImporterRequestItemsData;
}

interface DialogImportOrdersFromIntegrationContextImporterProgress {
  runListener: boolean;
  status: ImporterOrdersProgressListenerStatus;
  data: ImporterOrdersProgressNotification;
}

interface DialogImportOrdersFromIntegrationContextValue {
  activeTab: DialogImportOrdersFromIntegrationContextPanelsId;
  logisticsZones: DialogImportOrdersFromIntegrationContextLogisticsZones;
  currentIntegrations: DialogImportOrdersFromIntegrationContextCurrentIntegrations;
  importerRequest: DialogImportOrdersFromIntegrationContextImporterRequest;
  importerProgress: DialogImportOrdersFromIntegrationContextImporterProgress;
}

const DialogImportOrdersFromIntegrationContext =
  createContext<DialogImportOrdersFromIntegrationContextValue>({
    activeTab: 'notIntegrations',
    logisticsZones: {
      list: [],
      loading: false,
    },
    currentIntegrations: {
      list: [],
      loading: true,
    },
    importerRequest: {
      executeImport: () => Promise.resolve(),
      loading: false,
      isCompletedRequest: false,
      itemsData: { descriptions: [] },
    },
    importerProgress: {
      runListener: false,
      status: null,
      data: {
        progress: 0,
        step: 0,
        total: 0,
        integrationType: undefined,
      },
    },
  });

export const useDialogImportOrdersFromIntegrationContext =
  (): DialogImportOrdersFromIntegrationContextValue =>
    useContext(DialogImportOrdersFromIntegrationContext);

const DialogImportOrdersFromIntegrationProvider: FC<
  DialogImportOrdersFromIntegrationContextParams
> = ({ children, onSubmitSuccess, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isCarrier } = useTypePartnerCompany();
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const [activeTab, setActiveTab] =
    useState<DialogImportOrdersFromIntegrationContextPanelsId>(
      'notIntegrations',
    );

  const isValidToFetcLogisticzZoneS = useMemo(
    () => isCarrier && hasAccessToFeature(PlanFeaturesTypes.MgmZones),
    [isCarrier, hasAccessToFeature],
  );

  const [logisticsZones, setLogisticsZone] = useState<LogisticZoneBase[]>([]);
  const [, loadingLogisticsZone] = useRequest({
    request: getLogistiscZonesInfo,
    isValidToRequest: isValidToFetcLogisticzZoneS,
    initialLoading: isValidToFetcLogisticzZoneS,
    payload: null,
    withPostSuccess: (response) => {
      const logisitcisZoneResponse = response.data?.data
        .results as LogisticZoneBase[];

      const sortedLogisitcisZoneResponse = sortAlphabeticZoneByCode(
        logisitcisZoneResponse,
      );

      setLogisticsZone(sortedLogisitcisZoneResponse);
    },
  });

  const [currentIntegrationsList, setCurrentIntegrationsList] = useState<
    AdminIntegration[]
  >([]);

  const [, loadingCurrentIntegrationsIds] = useRequest({
    request: getIntegrations,
    payload: null,
    withPostSuccess: (response) => {
      const {
        data: { results },
      } = response.data as DataPaginateResponse<AdminIntegration[]>;

      const currentIntegrations = results.sort(
        (a, b) => a.integrationTypeId - b.integrationTypeId,
      );

      setCurrentIntegrationsList(currentIntegrations);

      if (currentIntegrations.length > 0) setActiveTab('presentation');
    },
  });

  const [runImporterProgressListener, setImporterProgressListener] =
    useState(false);

  const [isCompletedRequest, setIsCompletedRequest] = useState(false);
  const [importedItemsData, setImportedItemsData] =
    useState<DialogImportOrdersFromIntegrationContextImporterRequestItemsData>({
      descriptions: [],
    });

  const [, loadingExecuteImportItems, , executeImportItems] = useLazyRequest({
    request: importItemsFromIntegrations,
    withPostSuccess: (response) => {
      const { message, operations } = response.data
        ?.data as ItemImportFromIntegrationsResponse;

      if (operations.quantity >= 1) {
        const responseAlerts = chain(message)
          .split(/\n/)
          .slice(1)
          .uniqWith(isEmpty)
          .map(trimStart)
          .value();

        setImportedItemsData({
          descriptions: responseAlerts,
        });

        onSubmitSuccess();
      } else {
        enqueueSnackbar('No se registran operaciones para importar', {
          variant: 'info',
        });

        onClose();
      }

      setIsCompletedRequest(true);
    },
    withPostFailure: (err) => {
      let errorMessage = 'Ha ocurrido un error, intente nuevamente';

      if (err?.data?.data?.message) {
        errorMessage = err.data.data.message;
      }

      setIsCompletedRequest(true);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });

  const handleImport = useCallback(
    async (data: ItemImportFromIntegrationsBody) => {
      setImporterProgressListener(true);
      setIsCompletedRequest(false);
      await executeImportItems(data);
    },
    [executeImportItems],
  );

  const { status: statusProgress, data: dataProgress } =
    useImporterOrdersProgressListener({
      runListenerUpdates: runImporterProgressListener,
      onProgress: () => setActiveTab('progress'),
    });

  return (
    <DialogImportOrdersFromIntegrationContext.Provider
      value={{
        activeTab,
        logisticsZones: {
          list: logisticsZones,
          loading: loadingLogisticsZone,
        },
        currentIntegrations: {
          list: currentIntegrationsList,
          loading: loadingCurrentIntegrationsIds,
        },
        importerRequest: {
          executeImport: handleImport,
          loading: loadingExecuteImportItems,
          isCompletedRequest,
          itemsData: importedItemsData,
        },
        importerProgress: {
          runListener: runImporterProgressListener,
          status: statusProgress,
          data: dataProgress,
        },
      }}
    >
      {children}
    </DialogImportOrdersFromIntegrationContext.Provider>
  );
};

export default DialogImportOrdersFromIntegrationProvider;
